import {
  IconBook,
  IconCalculator,
  IconChat,
  IconClipboardCheck,
  IconFile,
  IconRadar,
  IconTrash,
} from '@shared/ui/icons';
import {
  BigNumber,
  CalculatorMetricRow,
  EmptyHint,
  Grid,
  Metric,
  Metrics,
  SectionLabel,
} from './styles';
import { TileShell, TileSkeleton } from './TileShell';

interface ToolsSectionProps {
  isLoading: boolean;
  kbCount: number;
  templatesTotal: number;
  kbTech: number;
  kbLegal: number;
  templatesActive: number;
  checksTotal: number;
  activeChecks: number;
  chatTotal: number;
  lastChat: { updated_at: string } | null;
  npaDigest: { counts: { total: number; critical: number } } | null | undefined;
}

export function ToolsSection({
  isLoading,
  kbCount,
  templatesTotal,
  kbTech,
  kbLegal,
  templatesActive,
  checksTotal,
  activeChecks,
  chatTotal,
  lastChat,
  npaDigest,
}: ToolsSectionProps) {
  const npaTotal7d = npaDigest?.counts.total ?? 0;
  const npaCritical7d = npaDigest?.counts.critical ?? 0;

  return (
    <>
      <SectionLabel>Инструменты</SectionLabel>
      <Grid>
        <TileShell
          to="/agents/statut/knowledge"
          delay={0}
          icon={IconBook}
          title="База знаний"
          note="НПА, ГОСТы, эталонные формы — единый RAG-корпус"
          accent
        >
          {isLoading ? (
            <TileSkeleton />
          ) : (
            <>
              <BigNumber>{kbCount + templatesTotal}</BigNumber>
              <Metrics>
                <Metric>
                  НПА и ГОСТы
                  <strong>{kbTech + kbLegal}</strong>
                </Metric>
                <Metric $tone={templatesActive > 0 ? 'success' : 'default'}>
                  Эталонные формы
                  <strong>{templatesActive}</strong>
                </Metric>
              </Metrics>
            </>
          )}
        </TileShell>

        <TileShell
          to="/agents/statut/metrics"
          delay={60}
          icon={IconClipboardCheck}
          title="Метрики качества"
          note="целевые показатели"
        >
          <EmptyHint>
            % принятых рисков, точность ссылок на НПА, 0 пропусков сроков, % классификации писем.
            Обновляется автоматически.
          </EmptyHint>
        </TileShell>

        <TileShell
          to="/agents/statut/calculator"
          delay={120}
          icon={IconCalculator}
          title="Калькулятор неустойки"
          note="ст.395 ГК / договорная неустойка"
        >
          <CalculatorMetricRow>
            <Metric>
              Ключевая ставка ЦБ
              <strong>16,0%</strong>
            </Metric>
            <Metric>
              Действует с<strong>2026-05</strong>
            </Metric>
          </CalculatorMetricRow>
          <EmptyHint>
            Расчёт процентов или неустойки за период. Результат прикладывается к претензии.
          </EmptyHint>
        </TileShell>

        <TileShell
          to="/agents/statut/compliance"
          delay={180}
          icon={IconClipboardCheck}
          title="Проверка документа"
          note="Соответствие проектной документации ГОСТам и НПА"
        >
          {isLoading ? (
            <TileSkeleton />
          ) : (
            <>
              <BigNumber>{checksTotal}</BigNumber>
              <Metrics>
                <Metric $tone={activeChecks > 0 ? 'warning' : 'default'}>
                  Активных
                  <strong>{activeChecks}</strong>
                </Metric>
                <Metric>
                  Завершено
                  <strong>{checksTotal - activeChecks}</strong>
                </Metric>
              </Metrics>
            </>
          )}
        </TileShell>

        <TileShell
          to="/agents/statut/chat"
          delay={180}
          icon={IconChat}
          title="Чат с агентом"
          note="Правовые вопросы по базе знаний"
        >
          {isLoading ? (
            <TileSkeleton />
          ) : (
            <>
              {lastChat ? (
                <EmptyHint>
                  Последняя сессия от {new Date(lastChat.updated_at).toLocaleString('ru-RU')}
                </EmptyHint>
              ) : (
                <EmptyHint>
                  Активных сессий нет. Начните диалог — задайте вопрос по нормам права или обсудите
                  конкретный документ.
                </EmptyHint>
              )}
              <Metrics>
                <Metric>
                  Всего сессий
                  <strong>{chatTotal}</strong>
                </Metric>
              </Metrics>
            </>
          )}
        </TileShell>

        <TileShell
          to="/agents/statut/documents"
          delay={220}
          icon={IconFile}
          title="Архив документов"
          note=" — все сгенерированные DOCX в одном месте"
        >
          <EmptyHint>
            Ответы на претензии, перевыставления субподрядчикам, протоколы разногласий.
            Фильтр по типу, переход к источнику, скачивание.
          </EmptyHint>
        </TileShell>

        <TileShell
          to="/agents/statut/monitoring"
          delay={240}
          icon={IconRadar}
          title="Мониторинг НПА"
          note=" — изменения законодательства за 7 дней"
        >
          {npaDigest == null ? (
            <TileSkeleton />
          ) : npaTotal7d === 0 ? (
            <>
              <BigNumber>—</BigNumber>
              <EmptyHint>За неделю значимых изменений не зафиксировано.</EmptyHint>
            </>
          ) : (
            <>
              <BigNumber>{npaTotal7d}</BigNumber>
              <Metrics>
                {npaCritical7d > 0 && (
                  <Metric $tone="danger">
                    Критично
                    <strong>{npaCritical7d}</strong>
                  </Metric>
                )}
                <Metric>
                  За 7 дней
                  <strong>{npaTotal7d}</strong>
                </Metric>
              </Metrics>
            </>
          )}
        </TileShell>

        <TileShell
          to="/agents/statut/trash"
          delay={300}
          icon={IconTrash}
          title="Корзина"
          note=" — удалённые записи, восстановление 30 дней"
        >
          <EmptyHint>
            Мягко удалённые документы, договоры, претензии, иски и ЛНА в одном месте. Хранятся
            30 дней, затем удаляются безвозвратно. Можно восстановить.
          </EmptyHint>
        </TileShell>
      </Grid>
    </>
  );
}
